import { useEffect, useMemo, useState } from "react";
import { X } from "@phosphor-icons/react";
import ListenTranslateBar from "./ListenTranslateBar";

/**
 * ChannelArticleModal — long-form editorial for one Observations channel.
 * Includes a Listen + Translate toolbar so any visitor can hear the article
 * or read it in their language.
 */
export default function ChannelArticleModal({ article, onClose }) {
  const [translated, setTranslated] = useState(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const fullText = useMemo(() => {
    if (!article) return "";
    return [
      article.title,
      article.subtitle,
      article.lede,
      ...article.sections.flatMap((s) => [s.heading, s.body]),
      article.outro,
    ].filter(Boolean).join("\n\n");
  }, [article]);

  if (!article) return null;

  // If translation returned, split it back into blocks lazily. For now we
  // show the whole translated blob in one panel rather than trying to split
  // into headings — much more reliable.
  return (
    <div
      className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-0 md:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="channel-modal-backdrop"
    >
      <article
        onClick={(e) => e.stopPropagation()}
        className="w-full md:max-w-3xl bg-[#FBF6EB] md:rounded-3xl overflow-hidden border border-black/10 shadow-2xl my-0 md:my-8"
        data-testid={`channel-modal-${article.slug || article.title}`}
      >
        <header className="sticky top-0 z-10 flex items-start gap-3 px-5 md:px-8 pt-5 md:pt-6 pb-4 bg-[#FBF6EB]/95 backdrop-blur border-b border-black/5">
          <div className="text-3xl leading-none shrink-0 mt-0.5">{article.emoji}</div>
          <div className="flex-1 min-w-0">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              / Observations · {article.title}
            </div>
            <h2 className="mt-1 font-serif-display text-2xl md:text-4xl leading-tight tracking-tight">
              {article.subtitle}
            </h2>
            <div className="mt-3">
              <ListenTranslateBar
                text={fullText}
                onTranslated={setTranslated}
                testidPrefix={`channel-modal-tools`}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="channel-modal-close"
            aria-label="Close"
            className="p-1.5 rounded-full hover:bg-black/5 text-[#241209] shrink-0"
          >
            <X size={20} weight="bold" />
          </button>
        </header>

        <div className="px-5 md:px-8 py-6 md:py-8">
          {translated ? (
            <div
              className="font-serif-display text-[16px] md:text-[17px] leading-relaxed text-[#241209] whitespace-pre-wrap"
              data-testid="channel-modal-translated"
            >
              {translated}
            </div>
          ) : (
            <>
              <p className="font-serif-display text-[16px] md:text-[17px] leading-relaxed text-[#241209] whitespace-pre-wrap">
                {article.lede}
              </p>
              {article.sections.map((s, i) => (
                <section key={i} className="mt-7">
                  <h3 className="font-serif-display text-xl md:text-2xl tracking-tight text-[#241209]">
                    {s.heading}
                  </h3>
                  <p className="mt-2 font-serif-display text-[15px] md:text-[16px] leading-relaxed text-[#241209] whitespace-pre-wrap">
                    {s.body}
                  </p>
                </section>
              ))}
              {article.outro && (
                <p className="mt-8 border-l-4 border-[color:var(--np-brand,#B85F2A)] pl-4 font-serif-display italic text-[15px] md:text-[16px] leading-relaxed text-[#3A3830]">
                  {article.outro}
                </p>
              )}
            </>
          )}
        </div>
      </article>
    </div>
  );
}
